import type { AgentConnection, Job, Scope } from '@cef-ai/vault-sdk';
import type { Signer } from '@cef-ai/signer';
import { Button, CircularProgress, EmptyState } from '@cere/cere-design-system';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import GridViewIcon from '@mui/icons-material/GridView';
import type { AgentCard } from '@/src/data/agent-cards';
import type { CereWallet } from '@/lib/clients/cere-wallet/connect';
import { marketplaceUrl } from '@/lib/marketplace/links';
import { ORGS_ANCHOR } from '@/src/data/anchors';
import { scopeLabelsByAgent } from '@/src/data/scope-grouping';
import { ActivityFeed } from '@/src/components/ActivityFeed';
import { AgentBlock } from '@/src/components/AgentBlock';
import { ScopesPage } from '@/src/components/scopes/ScopesPage';
import { OrgsSection } from '@/src/components/orgs/OrgsSection';
import { SettingsPage } from '@/src/components/SettingsPage';
import { BillingSection } from '@/src/components/billing/BillingSection';

interface Props {
  /** Agents in the shared recency order (same order the sidebar renders). */
  agents: AgentConnection[];
  agentCards: Record<string, AgentCard>;
  scopes: Scope[];
  jobs: Job[];
  agentsLoaded?: boolean;
  /** Widget signer (the Cere wallet's signingSigner) — bridged into widget iframes. */
  signer: Signer | null;
  onSelectAnchor: (anchor: string) => void;
  // Inline management blocks live at the bottom of the same scroll:
  settingsWallet: CereWallet;
  vaultId: string;
  vaultBaseUrl: string | null;
  delegateActive: boolean;
  onDelegateChange: (active: boolean) => void;
  onDisconnect: () => void;
  /** Per-agent disconnect — opens the confirm dialog owned by the parent. */
  onDisconnectAgent?: (agent: AgentConnection) => void;
  marketplaceBaseUrl: string;
}

/**
 * The entire one-scroll body: activity feed → one block per agent (recency
 * order, passed in) → a Scopes management block → a Billing block → a
 * Settings block. The sidebar is a table of contents over these same
 * anchors. Scopes/Billing/Settings are anchored sections rather than
 * separate routes/views, so everything is one continuous scroll.
 */
export function AgentsPage({
  agents, agentCards, scopes, jobs, agentsLoaded, signer, onSelectAnchor,
  settingsWallet, vaultId, vaultBaseUrl, delegateActive,
  onDelegateChange, onDisconnect, onDisconnectAgent, marketplaceBaseUrl,
}: Props) {
  const scopeLabel = scopeLabelsByAgent(agents, scopes);
  const empty = agents.length === 0;
  const loading = agentsLoaded === false;

  return (
    <div className="vault-onescroll">
      <ActivityFeed jobs={jobs} agents={agents} agentCards={agentCards} onSelectAnchor={onSelectAnchor} />

      {loading ? (
        <div className="vault-block vault-block--center">
          <CircularProgress size={28} />
        </div>
      ) : empty ? (
        <div className="vault-block vault-block--center">
          <EmptyState
            title="No agents connected yet"
            description="Browse the marketplace or follow a shared link to try an agent. Anything you connect shows up here, scoped to your Vault."
            icon={<AutoAwesomeIcon sx={{ fontSize: 34, color: 'var(--c-violet)' }} />}
            action={
              <Button
                variant="primary"
                href={marketplaceUrl(marketplaceBaseUrl, '/')}
                startIcon={<AddIcon sx={{ fontSize: 15, color: 'currentColor' }} />}
              >
                Browse Marketplace
              </Button>
            }
          />
        </div>
      ) : (
        <>
          <header className="vault-onescroll__section-head">
            <span className="vault-onescroll__section-icon" aria-hidden>
              <GridViewIcon sx={{ fontSize: 18, color: 'currentColor' }} />
            </span>
            <h2 className="vault-onescroll__section-title">Your agents</h2>
          </header>
          {agents.map((agent) => (
            <AgentBlock
              key={agent.agentId}
              agent={agent}
              card={agentCards[agent.agentId]}
              signer={signer}
              scopeLabel={scopeLabel.get(agent.agentId)}
              onDisconnect={onDisconnectAgent}
              onSelectAnchor={onSelectAnchor}
            />
          ))}
        </>
      )}

      <section className="vault-block vault-manage-block" data-anchor="section-scopes" aria-label="Scopes">
        <ScopesPage />
      </section>
      <section className="vault-block vault-manage-block" data-anchor={ORGS_ANCHOR} aria-label="Organizations">
        <OrgsSection />
      </section>
      <BillingSection />
      <section className="vault-block vault-manage-block" data-anchor="section-settings" aria-label="Settings">
        <SettingsPage
          wallet={settingsWallet}
          vaultId={vaultId}
          vaultBaseUrl={vaultBaseUrl}
          delegateActive={delegateActive}
          onDelegateChange={onDelegateChange}
          onDisconnect={onDisconnect}
        />
      </section>
    </div>
  );
}
